import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitCompare } from 'lucide-react';
import type { OwnerCompareReportDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';

type RepairAction = 'recapture' | 'redelete';

export function TenantComparePanel({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const key = ['tenant-sync-compare', tenantId];

  const report = useQuery({
    queryKey: key,
    queryFn: () => get<OwnerCompareReportDto | null>(`/owner/tenants/${tenantId}/sync/compare`),
    staleTime: 60_000,
  });

  const compare = useMutation({
    mutationFn: () => post(`/owner/tenants/${tenantId}/sync/compare`, {}),
    onSuccess: () => toast.success('Compare requested', 'The facility server compares itself with the online copy at its next check-in'),
    onError: (error) => toast.error('Could not request a compare', describeError(error)),
  });

  const repair = useMutation({
    mutationFn: (input: { table: string; action: RepairAction; ids: string[] }) => post(`/owner/tenants/${tenantId}/sync/repair`, { ...input, totpCode }),
    onSuccess: () => {
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: ['tenant-commands', tenantId] });
      toast.success('Repair queued', 'The facility server applies it at its next check-in');
    },
    onError: (error) => toast.error('Could not queue the repair', describeError(error)),
  });

  const data = report.data;
  const canRepair = isValidTotp(totpCode) && !repair.isPending;

  return (
    <Card className="lg:col-span-3">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-sm">
          <GitCompare className="h-4 w-4 text-primary" aria-hidden /> Compare with the facility&apos;s server
        </CardTitle>
        <Button variant="outline" size="sm" loading={compare.isPending} onClick={() => compare.mutate()}>
          Compare
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data && <p className="text-sm text-muted-foreground">This facility has not been compared with the online copy yet.</p>}
        {data && (
          <>
            <p className="text-xs text-muted-foreground">
              Last compared {dateTime(data.at)} across {data.tablesCompared} kinds of record
              {data.sinceSeq ? ', looking at what changed after the facility went back to an older backup' : ''}.
              {data.tables.length === 0 ? ' Everything matched.' : ''}
            </p>
            {data.tables.length > 0 && (
              <ul className="divide-y rounded-md border">
                {data.tables.map((table) => {
                  const resend = [...table.missingOnline, ...table.different];
                  return (
                    <li key={table.table} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-1 font-medium" title={table.table}>
                        {table.label}
                      </span>
                      {table.missingOnline.length > 0 && <Badge variant="warning">{table.missingOnline.length} missing online</Badge>}
                      {table.different.length > 0 && <Badge variant="warning">{table.different.length} different</Badge>}
                      {table.onlyOnline.length > 0 && <Badge variant="destructive">{table.onlyOnline.length} only online</Badge>}
                      {table.deletedOnline.length > 0 && <Badge variant="secondary">{table.deletedOnline.length} removed online after the backup</Badge>}
                      {table.truncated && <Badge variant="secondary">More not listed</Badge>}
                      {resend.length > 0 && (
                        <Button variant="outline" size="sm" disabled={!canRepair} onClick={() => repair.mutate({ table: table.table, action: 'recapture', ids: resend })}>
                          Send again
                        </Button>
                      )}
                      {table.onlyOnline.length > 0 && table.table !== 'audit_logs' && (
                        <Button variant="outline" size="sm" disabled={!canRepair} onClick={() => repair.mutate({ table: table.table, action: 'redelete', ids: table.onlyOnline })}>
                          Remove from online copy
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {data.tables.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground">
                  Records only the online copy holds are usually work done after the backup the facility went back to. Keep them unless you are sure
                  they are leftovers; the facility&apos;s server removes only records it does not hold.
                </p>
                <TotpField value={totpCode} onChange={setTotpCode} />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
